// @flow
import * as React from 'react';

import type { Currency } from '@gandi/billing';
import { selectCartGrid } from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';
import { useResourcePost } from '@gandi/resource/react';
import { useTranslator } from '@gandi/translate';

import { usePreferences } from 'src/Layout/hooks/usePreferences';
import { useAppSelector } from 'src/hooks/useAppSelector';

import {
  acmeCatalogResource,
  type AcmeCatalogResourceType,
  type Price,
} from '../modules/acme';

type GroupedItem = {
  duration: number,
  total: number,
  currency: Currency,
  unit: 'year' | 'month' | 'day',
};

const useBuildDurationOptions = (
  prices: Array<Price>,
): Array<{ value: string, label: string }> => {
  const { __, formatPrice } = useTranslator();
  const grouped = prices.reduce(
    (acc, item) => {
      const { duration_unit, min_duration, price_before_taxes, currency } =
        item;
      const key = String(min_duration);

      if (!acc[key]) {
        acc[key] = {
          duration: min_duration,
          total: 0,
          currency,
          unit: duration_unit,
        };
      }
      acc[key].total += price_before_taxes;
      return acc;
    },
    ({}: { [string]: GroupedItem }),
  );

  return Object.values(grouped).map(({ duration, unit, total, currency }) => {
    const label =
      unit === 'year'
        ? __('%(count)s years', { count: duration })
        : __('%(count)s months', { count: duration });
    const price = formatPrice(total, currency);

    return {
      value: String(duration),
      label: `${label} – ${price}`,
    };
  });
};

export function useQuote(): {
  options: Array<{ value: string, label: string }>,
  isPosting: boolean,
  handlePost: (
    body: AcmeCatalogResourceType['postPayload'],
  ) => Promise<AcmeCatalogResourceType['postResponse']>,
  pricingParams: { grid: ?string, country: ?string, currency: ?string },
} {
  const grid = useAppSelector(selectCartGrid);
  const pricingPreferences = usePreferences();
  const pricingParams = React.useMemo(
    () => ({
      country: pricingPreferences?.countryCode,
      currency: pricingPreferences?.currency,
      grid,
    }),
    [grid, pricingPreferences?.countryCode, pricingPreferences?.currency],
  );

  const sharingId = useAppSelector(selectCurrentId);
  const fetchParams = React.useMemo(
    () => (sharingId ? { sharing_id: sharingId } : {}),
    [sharingId],
  );

  const label = React.useMemo(
    () =>
      acmeCatalogResource.computeLabel(
        acmeCatalogResource.options.postPath,
        fetchParams,
      ),
    [fetchParams],
  );

  const { handlePost, isPosting } = useResourcePost(
    acmeCatalogResource,
    fetchParams,
  );
  const quote = useAppSelector(state =>
    acmeCatalogResource.selectAcmeCatalogResponse(state, label),
  );

  const options = useBuildDurationOptions(quote);

  return { options, isPosting, handlePost, pricingParams };
}
